import { useEffect } from 'react';
import Layout from '../../../../components/layout/Layout';
import Breadcrumb from '../../../../components/layout/Breadcrumb';
import { updateSEO } from '../../../../utils/seo';

// ===== How to Become a Butler (route=/en/butler/become) =====
// EN counterpart of /butler/become — structure, claims, and figures aligned with JP
// Design canon: navy #1a2942 / brass #a38e65 / gold #d7b779 / BTN-08
// Factual canon: three certification levels (Associate, Professional, Master)
//   + specialist qualifications + a distance-learning program
// No fabricated fees, pass rates, or graduate counts
// EN = no Japanese characters

const BTN08_ON_LIGHT =
  "relative inline-flex items-center justify-center bg-[#1a2942] text-[#f8f6f0] border border-[#a38e65] px-[42px] py-[16px] text-sm tracking-[0.2em] font-['Playfair_Display',serif] transition-colors duration-300 hover:bg-[#a38e65] hover:text-[#1a2942] hover:border-[#1a2942] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#a38e65]/45 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#1a2942]/45";
const BTN08_ON_NAVY =
  "relative inline-flex items-center justify-center bg-[#a38e65] text-white font-semibold border border-[#1a2942] px-[42px] py-[16px] text-sm tracking-[0.2em] font-['Playfair_Display',serif] transition-colors duration-300 hover:bg-[#1a2942] hover:text-[#f8f6f0] hover:border-[#a38e65] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#1a2942]/40 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#a38e65]/50";

export default function ButlerBecomePageEN() {
  useEffect(() => {
    updateSEO({
      title: 'How to Become a Butler | Certification Path | Japan Butler Association',
      description:
        'The Japan Butler Association outlines the path to becoming a butler: the aptitudes required, distance learning and in-person training, three certification levels—Associate, Professional, and Master—plus specialist qualifications, and continued practice after certification.',
      keywords:
        'become a butler, butler certification path, Associate Professional Master Butler, distance learning, Japan Butler Association',
      canonical: 'https://japanbutlerassociation.com/en/butler/become',
    });
  }, []);

  const breadcrumbs = [
    { label: 'Home', href: '/en' },
    { label: 'The Butler Profession', href: '/en/butler' },
    { label: 'How to Become a Butler' },
  ];

  const aptitudePillars = [
    {
      en: 'Observation',
      title: 'Anticipation & Awareness',
      text: 'Reading the room before a need is spoken, and quietly arranging the flow of the household. Butler service is not defined by reacting to requests; its essence lies in preparing one step ahead of what becomes visible.',
    },
    {
      en: 'Discretion',
      title: 'Confidentiality & Restraint',
      text: 'Because a butler enters the innermost circle of a household or organisation, the discipline never to carry what one sees or hears outside is non-negotiable. Dignity in silence, and the judgement to escalate only what must be reported, are both required.',
    },
    {
      en: 'Composure',
      title: 'Calm & Courtesy',
      text: 'Keeping tone and bearing steady even when the unexpected arrives. A bearing shaped by British discipline and the fine grain of Japanese courtesy becomes the foundation of lasting trust.',
    },
    {
      en: 'Stewardship',
      title: 'Responsibility to Keep Order',
      text: 'Holding a wide view of the home, the diary, the table, and the movement of people so that the principal and family can focus on what only they should address. The axis is not control, but the will to protect and support.',
    },
  ];

  const learningEntries = [
    {
      no: '01',
      title: 'Distance-Learning Programme',
      lead: 'Take the first step while keeping the rhythm of daily life',
      body: 'The Japan Butler Association offers a distance-learning programme as one of its gateways. Foundational theory, the thinking behind etiquette, and the frameworks of household management and hospitality are structured so that those who live far away or keep demanding schedules can still progress step by step. Study does not end in solitary reading: assignments train the ability to put into words why a given procedure exists.',
      href: '/en/qualifications/',
      linkLabel: 'Explore Certifications',
    },
    {
      no: '02',
      title: 'In-Person Training',
      lead: 'Form that the body remembers, and the reading of atmosphere',
      body: 'Handling formal attire, guest circulation, table service, and the manner of reporting—domains that text alone cannot fully convey—are refined in person. Through role-play with peers, voice, timing, and eye level are calibrated until one internalises the stance of being needed without drawing attention.',
      href: '/en/qualifications/',
      linkLabel: 'View the Certification System',
    },
  ];

  const associatePoints = [
    'Foundations of courtesy, personal presentation, and measured speech',
    'Introductory guest reception, message-taking, and seating order',
    'An overview of household management and the discipline of executing instructions accurately',
    'An introduction to confidentiality and professional ethics',
    'Patterns of reporting and liaison, and the habit of keeping records',
  ];

  const professionalPoints = [
    'The ability to keep quality steady while running several strands of work in parallel',
    'Practical response that includes on-the-spot judgement at the table, in hospitality, and on itineraries',
    'Quiet leadership that extends to coordinating juniors and external partners',
    'Proposal-led service shaped by the principal’s preferences and the house style',
    'The capacity to fold specialist knowledge into everyday operations',
  ];

  const masterPoints = [
    'Oversight of an entire household or organisation, and the development of people',
    'The ability to keep dignity under pressure and stabilise a room by setting direction',
    'Involvement in designing systems that bridge tradition and contemporary living',
    'A professional outlook that sets an example for those who follow, and a posture of continuous study',
    'Embodying, on the front line of practice, the butler ideal the Association upholds',
  ];

  const specialtyQuals = [
    { en: 'Business Manner', label: 'Business Manner' },
    { en: 'Dining', label: 'Dining Coordinator' },
    { en: 'Wine', label: 'Wine Steward' },
    { en: 'Tea', label: 'Tea Sommelier' },
    { en: 'Afternoon Tea', label: 'Afternoon Tea' },
    { en: 'Uniform', label: 'Butler-Style Uniform' },
    { en: 'Events', label: 'Event Planning' },
    { en: 'Household', label: 'Household Manager' },
    { en: 'Travel', label: 'Travel Concierge' },
  ];

  const faqItems = [
    {
      q: 'Are there age or educational requirements?',
      a: 'The Association’s certification routes set no restrictions by age, gender, or academic background. What matters is a spirit of service, the will to keep learning, and the posture of building trust through practice.',
    },
    {
      q: 'Which certification should I begin with?',
      a: 'Most candidates progress step by step from the entry-level Associate Butler. It is common to strengthen theory in parallel through the distance-learning programme, and—where useful—to deepen a chosen domain through specialist qualifications such as wine, tea, or household management.',
    },
    {
      q: 'What kind of continued study is expected after certification?',
      a: 'Certification is not an endpoint; it is the starting point for sustaining the quality of practice. Beyond reflection in the field, candidates continue to refine skill and character through additional specialist credentials and participation in the Association’s learning community.',
    },
  ];

  const articleLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: 'How to Become a Butler | Certification Path of the Japan Butler Association',
    description:
      'The path the Japan Butler Association sets out for becoming a butler: required aptitudes, distance learning and in-person training, three levels—Associate, Professional, and Master—plus specialist qualifications, and continued practice after certification.',
    author: {
      '@type': 'Organization',
      name: 'Japan Butler Association',
    },
    publisher: {
      '@type': 'Organization',
      name: 'Japan Butler Association',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': 'https://japanbutlerassociation.com/en/butler/become',
    },
  };

  const breadcrumbLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://japanbutlerassociation.com/en' },
      { '@type': 'ListItem', position: 2, name: 'The Butler Profession', item: 'https://japanbutlerassociation.com/en/butler' },
      { '@type': 'ListItem', position: 3, name: 'How to Become a Butler', item: 'https://japanbutlerassociation.com/en/butler/become' },
    ],
  };

  const faqLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.a,
      },
    })),
  };

  return (
    <Layout>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqLd) }} />

      {/* Hero — full-width billboard */}
      <section className="relative bg-[#1a2942] overflow-hidden min-h-[520px] lg:min-h-[600px] flex items-center">
        <img
          src="/images/generated/jba-butler-profession-hero.jpg"
          alt="A butler standing quietly in a refined interior, symbolising the starting point of a life in service"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(16,20,26,0.92)_0%,rgba(16,20,26,0.72)_48%,rgba(16,20,26,0.35)_100%)]"></div>
        <div className="relative z-10 w-full max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10 pt-32 pb-16 lg:pt-36 lg:pb-20">
          <div className="max-w-[720px]">
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.32em] text-xs sm:text-[13px] text-[#d7b779] mb-4">
              Path to Becoming a Butler
            </span>
            <div className="w-12 h-px bg-[#a38e65] mb-6"></div>
            <h1 className="font-['Playfair_Display',serif] font-medium text-white text-[clamp(28px,3.4vw,44px)] leading-[1.35] tracking-[0.02em] mb-6">
              <span className="block">How to Become a Butler</span>
              <span className="block text-[#d7b779] text-[clamp(20px,2.4vw,30px)] mt-2">The certification path the Association sets out</span>
            </h1>
            <p className="font-['Playfair_Display',serif] text-base sm:text-lg text-white/90 leading-[1.95]">
              Since its founding in 1890, the Japan Butler Association has worked to preserve butler culture in Japan and to develop the people who carry it. Under Representative Director Yuichi Ishii, third-generation head of the house, the Association holds as its canon three progressive levels—Associate, Professional, and Master—together with specialist qualifications, and presents a systematic path toward becoming a butler. No restrictions are set by age, gender, or academic background.
            </p>
          </div>
        </div>
      </section>

      <div className="bg-white [&>div]:!pt-6">
        <Breadcrumb items={breadcrumbs} />
      </div>

      {/* Body — single-column editorial layout */}
      <div className="bg-white">
        <div className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16 lg:py-20 space-y-20 sm:space-y-24">

          {/* S1. Overview of the route */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
              I. The Route
            </span>
            <h2 className="font-['Playfair_Display',serif] font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
              The certification route the Association <span className="text-[#a38e65]">holds as canon</span>
            </h2>
            <div className="space-y-[18px] font-['Playfair_Display',serif] text-base text-[#333] leading-[1.95]">
              <p>
                A butler is not made overnight. One acquires the forms of courtesy, grows judgement in the field, and eventually reaches a vantage point from which an entire household or organisation can be held in view. The Association makes that journey visible through its certification system. At the core of the route stand three levels: Associate Butler (entry), Professional Butler (intermediate), and Master Butler (advanced). Across them run specialist qualifications—wine, tea, afternoon tea, table presentation, household management, travel arrangements, and more—adding depth shaped by each person’s aptitude and by the demands of the workplace.
              </p>
              <p>
                The pages that follow guide you, in order, through the aptitudes required, the gateways into learning, what each level asks of a candidate, the place of specialist credentials, and the practice and continued study that follow certification. Begin by grasping the whole picture, then choose the next step from where you stand today.
              </p>
            </div>
            <ol className="mt-10 grid sm:grid-cols-3 border-t-2 border-[#1a2942]">
              {[
                { step: 'Step 01', label: 'Know the aptitudes', sub: 'Disposition and mindset' },
                { step: 'Step 02', label: 'Begin to learn', sub: 'Distance learning and in-person work' },
                { step: 'Step 03', label: 'Advance by level', sub: 'From entry to advanced' },
              ].map((s) => (
                <li key={s.step} className="border-b sm:border-b-0 sm:border-r last:border-r-0 border-[#e2ddd2] px-5 py-6">
                  <span className="block font-['Playfair_Display',serif] text-[#a38e65] text-sm tracking-[0.12em] mb-2">{s.step}</span>
                  <span className="block font-['Playfair_Display',serif] font-medium text-[#1a2942] text-lg mb-1">{s.label}</span>
                  <span className="block font-['Playfair_Display',serif] text-sm text-[#4a4638]">{s.sub}</span>
                </li>
              ))}
            </ol>
          </section>

          {/* S2. Aptitude */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
              II. Aptitude
            </span>
            <h2 className="font-['Playfair_Display',serif] font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
              The aptitudes and disposition required
            </h2>
            <div className="grid lg:grid-cols-[1fr_340px] gap-10 items-start mb-10">
              <div className="space-y-[18px] font-['Playfair_Display',serif] text-base text-[#333] leading-[1.95]">
                <p>
                  Technique can be learned later. Sensitivity to the presence of others, the habit of not placing oneself too far forward, and the quiet keeping of promises—these need already to be budding as daily posture. What the Association values is not brilliant self-assertion, but the quiet will to protect the time of the principal and family, and to keep a room running smoothly.
                </p>
                <p>
                  Flexibility—the willingness to relearn—is also part of aptitude. Lifestyles and tools change with the times, yet the core of anticipatory service does not. Those who honour established form while updating procedure to fit life as it is lived today are the ones who grow into butlers trusted over the long term.
                </p>
              </div>
              <img
                src="/images/generated/jba-butler-team-briefing.jpg"
                alt="Butlers briefing before training, sharing observation and sequencing"
                className="w-full h-64 lg:h-[300px] object-cover border border-[#a38e65]/30"
              />
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              {aptitudePillars.map((p) => (
                <div key={p.en} className="bg-white border border-[#e2ddd2] shadow-[0_12px_40px_rgba(26,41,66,0.06)] px-6 py-7">
                  <span className="block font-['Playfair_Display',serif] italic text-[#8f7a56] text-[13px] tracking-[0.08em] mb-2">{p.en}</span>
                  <h3 className="font-['Playfair_Display',serif] font-medium text-[#1a2942] text-lg sm:text-xl mb-3">{p.title}</h3>
                  <p className="font-['Playfair_Display',serif] text-sm text-[#4a4638] leading-[1.9]">{p.text}</p>
                </div>
              ))}
            </div>
          </section>

          {/* S3. Gateways to learning */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
              III. Gateways to Learning
            </span>
            <h2 className="font-['Playfair_Display',serif] font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-3">
              Gateways to learning — distance study and in-person training
            </h2>
            <p className="font-['Playfair_Display',serif] text-base text-[#333] leading-[1.95] mb-8 max-w-3xl">
              There is no single entrance. Some candidates secure theory first; others begin from bodily sense; many move back and forth between the two. The distance-learning programme and in-person training are not opposites. They are designed as twin wheels, each supplying what the other cannot fully carry alone.
            </p>
            <div className="bg-[#1a2942] px-6 sm:px-8 py-4 mb-8 flex flex-wrap items-center gap-x-8 gap-y-2">
              <span className="font-['Playfair_Display',serif] text-[#d7b779] tracking-[0.18em] text-sm">LEARNING PATH</span>
              <span className="font-['Playfair_Display',serif] text-[#f8f6f0]/85 text-sm tracking-[0.06em]">
                Secure the thinking at a distance; refine the form in person
              </span>
            </div>
            <div className="grid lg:grid-cols-2 gap-8">
              {learningEntries.map((e) => (
                <div key={e.no} className="border border-[#e2ddd2] bg-[#fbfaf7] p-7 sm:p-8 flex flex-col">
                  <div className="flex items-baseline gap-4 mb-4">
                    <span className="font-['Playfair_Display',serif] text-[#d7b779] text-3xl leading-none">{e.no}</span>
                    <h3 className="font-['Playfair_Display',serif] font-medium text-[#1a2942] text-xl">{e.title}</h3>
                  </div>
                  <p className="font-['Playfair_Display',serif] text-[#8f7a56] text-sm tracking-[0.04em] mb-3">{e.lead}</p>
                  <p className="font-['Playfair_Display',serif] text-[15px] text-[#333] leading-[1.95] flex-1 mb-6">{e.body}</p>
                  <a href={e.href} className={BTN08_ON_LIGHT}>
                    {e.linkLabel}
                  </a>
                </div>
              ))}
            </div>
            <div className="mt-8">
              <img
                src="/images/generated/jba-become-foundation-training.jpg"
                alt="Foundation training in progress, with careful study of courtesy and form"
                className="w-full h-56 sm:h-72 object-cover border border-[#a38e65]/30"
              />
            </div>
          </section>

          {/* S4. Three grades */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
              IV. Three Grades
            </span>
            <h2 className="font-['Playfair_Display',serif] font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
              What the three certification levels ask of you
            </h2>
            <p className="font-['Playfair_Display',serif] text-base text-[#333] leading-[1.95] mb-10 max-w-3xl">
              Associate Butler (entry), Professional Butler (intermediate), and Master Butler (advanced) form one continuous path—from the forms of courtesy, through judgement in the field, to the responsibility of guiding a household or organisation as a whole. What each level examines is set out below in order.
            </p>

            {/* Associate */}
            <div className="mb-14 sm:mb-16">
              <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
                Associate Butler
              </span>
              <h3 className="font-['Playfair_Display',serif] font-medium text-[#1a2942] text-xl sm:text-2xl leading-[1.5] tracking-[0.02em] mb-5">
                What Associate Butler (entry level) examines
              </h3>
              <div className="grid lg:grid-cols-[1.1fr_0.9fr] gap-10 items-start">
                <div>
                  <p className="font-['Playfair_Display',serif] text-base text-[#333] leading-[1.95] mb-6">
                    The entry level is the stage at which one acquires the shared language needed to stand on the floor as a butler. More than brilliant presentation, it settles—without gaps—the foundations of greeting, message-taking, personal presentation, the receiving of instruction, and an awareness of confidentiality. Here, accuracy is valued above speed, and understanding of form above personal improvisation.
                  </p>
                  <ul className="border-t border-[#1a2942]">
                    {associatePoints.map((line) => (
                      <li
                        key={line}
                        className="flex gap-4 border-b border-[#e2ddd2] py-4 font-['Playfair_Display',serif] text-[15px] text-[#1a2942] leading-[1.85]"
                      >
                        <span className="text-[#a38e65] shrink-0 mt-0.5">▸</span>
                        <span>{line}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-8">
                    <a href="/en/qualifications/associate-butler" className={BTN08_ON_LIGHT}>
                      Associate Butler Details
                    </a>
                  </div>
                </div>
                <div className="border-l-2 border-[#a38e65] pl-6 sm:pl-8">
                  <p className="font-['Playfair_Display',serif] italic text-[#8f7a56] text-sm mb-3">Foundation first</p>
                  <p className="font-['Playfair_Display',serif] text-[15px] text-[#4a4638] leading-[1.95] mb-6">
                    The forms cultivated at entry level become the skeleton that supports judgement at intermediate level and beyond. Over a long professional life, building a state in which basic movements can be reproduced with beauty is, in the end, a shorter path than rushing toward advanced territory.
                  </p>
                  <img
                    src="/images/generated/jba-butler-quality-control.jpg"
                    alt="Hands checking the quality of service—accuracy and repeatability at the heart of entry-level practice"
                    className="w-full h-52 object-cover border border-[#a38e65]/30"
                  />
                </div>
              </div>
            </div>

            {/* Professional */}
            <div className="mb-14 sm:mb-16">
              <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
                Professional Butler
              </span>
              <h3 className="font-['Playfair_Display',serif] font-medium text-[#1a2942] text-xl sm:text-2xl leading-[1.5] tracking-[0.02em] mb-5">
                What Professional Butler (intermediate) examines
              </h3>
              <div className="grid lg:grid-cols-[320px_1fr] gap-10 items-start">
                <img
                  src="/images/generated/jba-become-professional-practice.jpg"
                  alt="An intermediate butler reviewing circulation and sequencing in a working household"
                  className="w-full h-64 lg:h-full min-h-[260px] object-cover border border-[#a38e65]/30"
                />
                <div>
                  <p className="font-['Playfair_Display',serif] text-base text-[#333] leading-[1.95] mb-6">
                    At the intermediate level, the question is whether one can step beyond waiting for instruction and set priorities by reading the situation. The flow of the table, guest reception, preparation for travel or events, coordination with outside partners—holding several threads at once while remaining quiet before the principal. That balance is the core of the Professional grade.
                  </p>
                  <div className="grid sm:grid-cols-1 gap-0 border-t border-[#e2ddd2]">
                    {professionalPoints.map((line, idx) => (
                      <div key={line} className="flex gap-5 py-4 border-b border-[#e2ddd2]">
                        <span className="font-['Playfair_Display',serif] text-[#d7b779] text-xl shrink-0 w-8">
                          {String(idx + 1).padStart(2, '0')}
                        </span>
                        <p className="font-['Playfair_Display',serif] text-[15px] text-[#1a2942] leading-[1.85]">{line}</p>
                      </div>
                    ))}
                  </div>
                  <div className="mt-8">
                    <a href="/en/qualifications/professional-butler" className={BTN08_ON_LIGHT}>
                      Professional Butler Details
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Master */}
            <div>
              <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
                Master Butler
              </span>
              <h3 className="font-['Playfair_Display',serif] font-medium text-[#1a2942] text-xl sm:text-2xl leading-[1.5] tracking-[0.02em] mb-6">
                What Master Butler (advanced) examines
              </h3>
              <div className="bg-[#1a2942] p-8 sm:p-10 lg:p-12">
                <div className="grid lg:grid-cols-[1fr_300px] gap-10 items-start">
                  <div>
                    <p className="font-['Playfair_Display',serif] text-base text-[#e7e2d8] leading-[1.95] mb-8">
                      The advanced level is at once the summit of individual skill and the stage of responsibility for guiding those who follow and the household as a whole. More than performing perfectly on the front line oneself, one is asked to set the standard, develop people, and never lose direction in a crisis. The Master Butler is positioned as the figure who embodies, in practice, the butler ideal the Association upholds.
                    </p>
                    <ul className="space-y-4">
                      {masterPoints.map((line) => (
                        <li key={line} className="flex gap-3 font-['Playfair_Display',serif] text-[15px] text-[#f8f6f0] leading-[1.85]">
                          <span className="text-[#a38e65] shrink-0">—</span>
                          <span>{line}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="mt-10">
                      <a href="/en/qualifications/master-butler" className={BTN08_ON_NAVY}>
                        Master Butler Details
                      </a>
                    </div>
                  </div>
                  <img
                    src="/images/generated/jba-become-master-leadership.jpg"
                    alt="The composed bearing of a senior butler whose presence guides those who follow"
                    className="w-full h-56 lg:h-[340px] object-cover border border-[#a38e65]/40"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* S5. Specialist credentials */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
              V. Specialist Credentials
            </span>
            <h2 className="font-['Playfair_Display',serif] font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
              The place of specialist qualifications
            </h2>
            <div className="space-y-[18px] font-['Playfair_Display',serif] text-base text-[#333] leading-[1.95] mb-8">
              <p>
                Where the three core levels speak to overall capability as a butler, specialist qualifications attest to depth in a defined domain. Wine and tea, afternoon tea, table presentation, household management, travel arrangements, business manner, uniform, and event planning—each theme connects directly to the principal’s way of life.
              </p>
              <p>
                Specialist credentials are not a substitute for the core path. After form is secured at Associate level, they function as lateral expansion, taken across domains according to workplace demand and personal interest. From Professional level onward, the field asks for the integrative power to dissolve that specialist knowledge naturally into daily operations.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[#e2ddd2] border border-[#e2ddd2]">
              {specialtyQuals.map((q) => (
                <a
                  key={q.label}
                  href="/en/qualifications/"
                  className="bg-white px-5 py-6 hover:bg-[#f8f6f0] transition-colors group"
                >
                  <span className="block font-['Playfair_Display',serif] italic text-[#8f7a56] text-[12px] tracking-[0.08em] mb-2">
                    {q.en}
                  </span>
                  <span className="font-['Playfair_Display',serif] font-medium text-[#1a2942] text-base group-hover:text-[#a38e65] transition-colors">
                    {q.label}
                  </span>
                </a>
              ))}
            </div>
            <p className="mt-6 font-['Playfair_Display',serif] text-sm text-[#4a4638] leading-[1.9]">
              Learning goals and content for each specialist qualification can be reviewed within the broader certification system. We recommend beginning with an overview from the{' '}
              <a href="/en/qualifications/" className="text-[#1a2942] underline underline-offset-4 decoration-[#a38e65] mx-1">
                certifications index
              </a>
              .
            </p>
          </section>

          {/* S6. Practice and continuance */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
              VI. Practice &amp; Continuance
            </span>
            <h2 className="font-['Playfair_Display',serif] font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
              Practice after certification and continued study
            </h2>
            <div className="space-y-[18px] font-['Playfair_Display',serif] text-base text-[#333] leading-[1.95] mb-10">
              <p>
                The moment a credential is in hand is when true practice begins. Whether one enters a private household or works in corporate hospitality and estate management, the work of learning the other party’s style—and adjusting one’s own form to that house—continues. A credential conferred by the Association is both proof of capability and a public promise not to let quality slip.
              </p>
              <p>
                The shape of continued study differs from person to person. Additional specialist credentials, a return to theory through distance learning, case-sharing with colleagues, and the recording of feedback from the principal—each becomes a habit that raises the precision of service that is meant to remain unseen. Joining the learning community through membership is also an effective way to support a field role that can otherwise become isolating.
              </p>
              <p>
                It bears repeating that the Association’s certification routes set no restrictions by age, gender, or academic background. At any point in life, the path of service remains open. What matters is choosing the learning you need today, and building level upon level with steady care.
              </p>
            </div>
            <dl className="grid sm:grid-cols-3 gap-6 border-t border-[#e2ddd2] pt-8">
              <div>
                <dt className="font-['Playfair_Display',serif] text-xs text-[#8f7a56] tracking-[0.06em] mb-2">Fields of practice</dt>
                <dd className="font-['Playfair_Display',serif] text-[15px] text-[#1a2942] leading-[1.85]">
                  In private homes, corporate settings, and events—matched to credential and aptitude—anticipatory service is put into practice.
                </dd>
              </div>
              <div>
                <dt className="font-['Playfair_Display',serif] text-xs text-[#8f7a56] tracking-[0.06em] mb-2">A cycle of study</dt>
                <dd className="font-['Playfair_Display',serif] text-[15px] text-[#1a2942] leading-[1.85]">
                  Insights from the field return to learning; specialist credentials and renewed study keep theory current.
                </dd>
              </div>
              <div>
                <dt className="font-['Playfair_Display',serif] text-xs text-[#8f7a56] tracking-[0.06em] mb-2">An open entrance</dt>
                <dd className="font-['Playfair_Display',serif] text-[15px] text-[#1a2942] leading-[1.85]">
                  Regardless of age, gender, or academic background, the certification route is open to those with the will to serve.
                </dd>
              </div>
            </dl>
          </section>

          {/* S7. FAQ */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
              VII. Questions
            </span>
            <h2 className="font-['Playfair_Display',serif] font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
              Frequently asked questions
            </h2>
            <div className="border-t-2 border-[#1a2942]">
              {faqItems.map((item) => (
                <div key={item.q} className="border-b border-[#e2ddd2] py-6">
                  <h3 className="font-['Playfair_Display',serif] font-medium text-[#1a2942] text-lg mb-3 flex gap-3">
                    <span className="text-[#a38e65] font-['Playfair_Display',serif] shrink-0">Q</span>
                    <span>{item.q}</span>
                  </h3>
                  <p className="font-['Playfair_Display',serif] text-[15px] text-[#333] leading-[1.95] pl-8">
                    {item.a}
                  </p>
                </div>
              ))}
            </div>
          </section>

          {/* S8. CTA */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
              VIII. Next Steps
            </span>
            <h2 className="font-['Playfair_Display',serif] font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
              Choose your next step
            </h2>
            <div className="bg-[#1a2942] border border-[#a38e65]/45 p-8 sm:p-10 lg:p-12">
              <p className="font-['Playfair_Display',serif] text-base sm:text-lg text-[#e7e2d8] leading-[1.95] max-w-3xl mb-8">
                Learn the background of the butler profession, compare certification details, or step into a community of learning—enter through the door closest to your interest. On a lineage that has continued since 1890, the Japan Butler Association keeps developing butlers suited to the present age.
              </p>
              <div className="flex flex-col sm:flex-row flex-wrap gap-4 sm:gap-6">
                <a href="/en/butler/" className={BTN08_ON_NAVY}>
                  The Butler Profession
                </a>
                <a href="/en/qualifications/" className={BTN08_ON_NAVY}>
                  Start with Certifications
                </a>
                <a href="/en/membership/" className={BTN08_ON_NAVY}>
                  View Membership
                </a>
              </div>
            </div>
          </section>

        </div>
      </div>
    </Layout>
  );
}
